// src/pages/products/CreateProduct/components/BasicInfoTab.jsx
import React from 'react';
import { Package } from 'lucide-react';
import { useLocalization } from '../../../../contexts/LocalizationContext';
import CategorySelector from '../../../../components/product/CategorySelector/CategorySelector';
import TagSelector from '../../../../components/product/TagSelector/TagSelector';
import BrandSelector from '../../../../components/product/BrandSelector/BrandSelector';
import RichTextEditor from '../../../../components/ui/RichTextEditor/RichTextEditor';
import styles from '../styles/BasicInfoTab.module.css';

const BasicInfoTab = ({
  formData,
  errors,
  handleInputChange,
  handleDescriptionChange,
  handleCategorySelect,
  handleTagsChange,
  handleBrandChange
}) => {
  const { t } = useLocalization();

  return (
    <div className={styles.formCard}>
      <div className={styles.cardHeader}>
        <h2>{t('products.basicInformation')}</h2>
        <p>{t('products.basicInformationDesc')}</p>
      </div>
      <div className={styles.cardContent}>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="name">{t('products.productName')} *</label>
            <div className={styles.inputWithIcon}>
              <Package size={18} className={styles.inputIcon} />
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('products.productNamePlaceholder')}
                className={errors.name ? 'error' : ''}
                required
              />
            </div>
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="printableName">{t('products.printableName')}</label>
            <input
              type="text"
              id="printableName"
              name="printableName"
              value={formData.printableName}
              onChange={handleInputChange}
              placeholder={t('products.printableNamePlaceholder')}
            />
            <span className={styles.formHint}>{t('products.printableNameHint')}</span>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label htmlFor="productType">{t('products.productType')} *</label>
            <select
              id="productType"
              name="productType"
              value={formData.productType}
              onChange={handleInputChange}
              className={errors.productType ? 'error' : ''}
              required
            >
              <option value="simple">{t('products.simpleProduct')}</option>
              <option value="variable">{t('products.variableProduct')}</option>
              <option value="bundle">{t('products.bundleProduct')}</option>
            </select>
            {errors.productType && <span className={styles.errorText}>{errors.productType}</span>}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">{t('products.description')}</label>
          <RichTextEditor
            value={formData.description}
            onChange={handleDescriptionChange}
            placeholder={t('products.descriptionPlaceholder')}
            error={errors.description}
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{t('products.categories')} *</label>
            <CategorySelector
              selectedCategoryId={formData.categoryId}
              onCategorySelect={handleCategorySelect}
              placeholder={t('products.chooseCategory')}
              error={errors.categoryId}
              allowCreateNew={true}
            />
          </div>

          <div className={styles.formGroup}>
            <label>{t('products.tags')}</label>
            <TagSelector
              selectedTagIds={formData.tagIds}
              onTagsChange={handleTagsChange}
              placeholder={t('products.selectProductTags')}
              error={errors.tagIds}
              allowCreateNew={true}
            />
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label>{t('products.brand')}</label>
            <BrandSelector
              selectedBrandId={formData.brandId || null}
              onBrandChange={handleBrandChange}
              placeholder={t('products.selectBrand')}
              error={errors.brandId}
              allowCreateNew={true}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="visibility">{t('products.visibility')} *</label>
            <select
              id="visibility"
              name="visibility"
              value={formData.visibility}
              onChange={handleInputChange}
              className={errors.visibility ? 'error' : ''}
              required
            >
              <option value="public">{t('products.public')}</option>
              <option value="draft">{t('products.draft')}</option>
              <option value="trash">{t('products.trash')}</option>
            </select>
            {errors.visibility && <span className={styles.errorText}>{errors.visibility}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoTab;